import {
	Dialog,
	DialogHeader,
	DialogDescription,
	DialogTitle,
	DialogContent,
	DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useForm, useStore } from "@tanstack/react-form";
import { z } from "zod";
import {
	ConnectionHandler,
	graphql,
	useFragment,
	useMutation,
} from "react-relay";
import { AnalyticsEvents, useAnalytics } from "@/utils/analytics";
import { toast } from "sonner";
import { Radio, RadioGroup } from "@/components/radio";
import { Field, FieldLabel } from "@/components/ui/field";
import type { postMoveDialogMutation } from "./__generated__/postMoveDialogMutation.graphql";
import type { postMoveDialogQuery$key } from "./__generated__/postMoveDialogQuery.graphql";

type MoveSpaceDialogProps = {
	open: boolean;
	onClose: () => void;
	post: postMoveDialogQuery$key;
};

export function MoveSpaceDialog({ open, onClose, post }: MoveSpaceDialogProps) {
	const { track } = useAnalytics();
	const data = useFragment<postMoveDialogQuery$key>(
		graphql`
    fragment postMoveDialogQuery on Post {
        id
        space {
          id
        }
        club {
          slug
          groups {
            id
            name
            spaces {
              id
              name
              icon
            }
          }
        }
    }
  `,
		post,
	);
	const [movePost, _isPending] = useMutation<postMoveDialogMutation>(graphql`
    mutation postMoveDialogMutation($id: ID!, $spaceId: ID!, $club: String!) {
      movePost(id: $id, spaceId: $spaceId) {
        ...postPreviewFragment @arguments(club: $club)
      }
    }
  `);

	const form = useForm({
		defaultValues: {
			space: data.space?.id,
		},
		validators: {
			onChange: z.object({
				space: z.enum(
					data.club.groups
						.filter(Boolean)
						.flatMap((g) => g.spaces.filter(Boolean).map((s) => s.id)),
				),
			}),
		},
		onSubmit: ({ value }) => {
			return movePost({
				variables: {
					id: data.id,
					spaceId: value.space!,
					club: data.club.slug,
				},
				onCompleted: () => {
					track(AnalyticsEvents.POST_MOVED, {
						post_id: data.id,
						to_space_id: value.space!,
					});
					toast.success("Post movido com sucesso!");
					onClose();
				},
				onError: () => {
					toast.error(
						"Ocorreu um erro ao mover o post. Tente novamente mais tarde.",
					);
				},
				updater: (store) => {
					const orderByOptions = [
						"BEST",
						"NEWEST",
						"OLDEST",
						"TRENDING",
					] as const;

					// Invalidate connections on the source feed so the post is removed from its listing
					const oldSpaceItem = data.space?.id ? store.get(data.space.id) : null;
					if (oldSpaceItem) {
						for (const orderBy of orderByOptions) {
							ConnectionHandler.getConnection(
								oldSpaceItem,
								"spaceFeedFragment__feed",
								{ ignoreFixed: false, orderBy },
							)?.invalidateRecord();
						}
					}

					// Invalidate connections on the destination feed so the post appears there
					const newSpaceItem = value.space ? store.get(value.space) : null;
					if (newSpaceItem) {
						for (const orderBy of orderByOptions) {
							ConnectionHandler.getConnection(
								newSpaceItem,
								"spaceFeedFragment__feed",
								{ ignoreFixed: false, orderBy },
							)?.invalidateRecord();
						}
					}
				},
			});
		},
	});

	const selectedSpaceId = useStore(form.store, (state) => state.values.space);

	const newFeed = data.club.groups
		.flatMap((g) => g.spaces.filter(Boolean))
		.find((s) => s.id === selectedSpaceId);

	const isSameFeed = selectedSpaceId === data.space?.id;

	return (
		<Dialog open={open} onOpenChange={onClose}>
			<DialogContent>
				<form
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
					className="flex flex-col gap-6"
				>
					<DialogHeader>
						<DialogTitle>Mover Post de Feed</DialogTitle>
					</DialogHeader>

					<DialogDescription>
						Escolha o feed para qual esse post será movido.
					</DialogDescription>

					<form.Field name="space">
						{(field) => (
							<div className="h-full max-h-[250px] overflow-y-auto border rounded p-3">
								{data.club.groups
									.filter(
										(group) => group && group.spaces.filter(Boolean).length > 0,
									)
									.map((group) => (
										<div key={group.id} className="mb-5 last:mb-0">
											<span className="text-sm/6 font-semibold">
												{group.name}
											</span>

											<RadioGroup
												className="mt-3"
												value={field.state.value}
												onChange={(val) => field.handleChange(val)}
											>
												{group.spaces.filter(Boolean).map((space) => (
													<Field
														key={space.id}
														className="flex flex-row items-center justify-start gap-2"
													>
														<Radio value={space.id} />
														<FieldLabel>
															{`${space.icon || ""} ${space.name}`.trim()}
														</FieldLabel>
													</Field>
												))}
											</RadioGroup>
										</div>
									))}
							</div>
						)}
					</form.Field>

					<DialogFooter>
						<Button variant="ghost" onClick={onClose}>
							Cancelar
						</Button>
						<Button disabled={!newFeed || isSameFeed} color="primary">
							Mover para{" "}
							{newFeed ? `${newFeed.icon || ""} ${newFeed.name}`.trim() : "..."}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
